import React from "react";
import {
  ENUM_FORM_DISPLAY,
  ENUM_FORM_LAYOUT_CONTAINER,
} from "../../components/pageList";
import {
  Text,
  TextInput,
  Select,
  Textarea,
} from "@mantine/core";
import { IconSettings } from "@tabler/icons";
import RemoteSelect2 from "../../components/remoteSelect2";
import { DatePicker } from "@mantine/dates";
import LicenceCheckList from "../../components/licenceCheckList";
import SfcLicenceCheckList from "../../components/sfcLicenceCheckList";
import ImagePicker from "../../components/imagePicker";
import FileList from "../../components/fileList";
import UserFileGroupList from "../../components/userFileGroupList";
import ConsultantCertificateList from "../../components/consultantCertificateList";
import UserActionList from "../../components/userActionList";
import ConsultantAllowanceList from "../../components/consultantAllowanceList";
import ConsultantOverridingPlans from "../../components/consultantOverridingPlans";
import Chips from "../../components/chips";
import DummyWidget from "../../components/dummyWidget";
import UniqueTextInput from "../../components/uniqueTextInput";
import UserClientList from "../../components/userClientList";
import UserCPDDetail from "../../components/userCPDDetail";
import ProviderLoginInfoRow from "../../components/providerLoginInfoRow.js";
import PersonalTradingAccountRow, {
  PersonalTradingAccountListHeader,
} from "../../components/personalTradingAccountRow.js";
import OrderList from "../../components/orderList.js";
import {
  DATA_GENDERS,
  DATA_MARIAGE_STATUS,
  DATA_EDUCATION_LEVEL,
  DATA_CONSULTANT_PAYMENT_METHOD,
  DATA_USER_TYPES,
  DATA_USER_CATEGORY,
  DATA_USER_DISPOSAL_STATUS,
  DATA_BANK_WITH_CODE,
} from "../../data/options";
import { LICENCE_ROLE_OPTIONS, MPFA_ROLE_OPTIONS, SFC_LICENCE_ROLE_OPTIONS } from "../../data/licenceRoles";
import UserOrderHistory from "../../components/userOrderHistory";
import ActivityLog from "../../components/activityLog";
import UserBasicInfo from "../../components/userBasicInfo";
import PhoneNumberList from "../../components/phoneNumberList";
import EmailList from "../../components/emailList";
import AddressList from "../../components/addressList";
import ContactList from "../../components/contactList";
import UserEmploymentInfo from "../../components/userEmploymentInfo";

const apiEntity = "user";
const initialValues = {
  address: "",
  isConsultant: true,
  files: [],
  fileGroups: [],
  userCategory: "INDIVIDUAL",
  userType: "CONSULTANT",
  idType: "HKID",
  consultantInfo: {
    licences: { l1: false, l2: false, l3: false, l4: false, l5: false },
    sfcLicences: { type1: false, type4: false, type9: false },
    iaRole: "TR",
    mpfaRole: "SI",
    sfcRole: "LR",
    isChannel: false,
    certificate: [],
    paymentMethods: { type: "", accNumber: "", name: "", bank: "" },
    personalTradingAccounts: [],
  },
  termination: {},
  phones: [],
  emails: [],
  addresses: [],
};

export const PROFILE_TAB_KEYS = [
  "tab-overview",
  "tab-bankInfo",
  "tab-licences",
  "tab-cpd",
  "tab-providerLogin",
  "tab-files",
];

const applyUserDetailOptions = (schema, options = {}) => {
  const {
    tabKeys = null,
    includeActionList = true,
    includeOverriding = true,
    readOnly = false,
    editableProviderLogin = false,
    showSfcLicencesInCpd = true,
    consultantViewFiles = false,
    hideInternalFiles = false,
    showProfileSummary = null,
  } = options;

  const useProfileSummary =
    showProfileSummary !== null ? showProfileSummary : readOnly;

  let containers = [...schema.layout.containers];
  let fields = [...schema.layout.fields];

  const mainTab = containers.find((c) => c.key === "main-tab");
  let tabs = mainTab?.tab ? [...mainTab.tab] : [];

  const mainTabKeySet = new Set((mainTab?.tab || []).map((tab) => tab.key));

  if (tabKeys) {
    tabs = tabs.filter((tab) => tabKeys.includes(tab.key));
    const allowedTabs = new Set(tabKeys);
    containers = containers.filter((c) => {
      if (c.key === "section-ops" && !includeOverriding) return false;
      if (c.parent?.startsWith("tab-") && mainTabKeySet.has(c.parent)) {
        return allowedTabs.has(c.parent);
      }
      return true;
    });
  }

  if (!includeOverriding) {
    containers = containers.filter((c) => c.key !== "section-ops");
  }

  if (hideInternalFiles) {
    containers = containers.filter((c) => c.key !== "section-files");
  }

  if (useProfileSummary) {
    containers = containers.filter(
      (c) => c.key !== "basicInfo-g" && c.key !== "employmentEdit-g"
    );
  }

  containers = containers.map((c) =>
    c.key === "main-tab" ? { ...c, tab: tabs } : c
  );

  const containerKeys = new Set(containers.map((c) => c.key));
  containerKeys.add("main-g1");

  fields = fields.filter((f) => {
    if (!useProfileSummary && ["basic", "basic3"].includes(f.name)) return false;
    if (f.name === "UserActionList" && !includeActionList) return false;
    if (hideInternalFiles && f.name === "files") return false;
    if (
      !includeOverriding &&
      ["parent", "consultantInfo.overridingPlan", "overridingPlans"].includes(
        f.name
      )
    )
      return false;
    if (tabKeys) {
      if (f.parent?.startsWith("tab-") && mainTabKeySet.has(f.parent)) {
        return tabKeys.includes(f.parent);
      }
      if (f.parent?.startsWith("tab-")) {
        return true;
      }
      return containerKeys.has(f.parent);
    }
    return true;
  });

  if (!showSfcLicencesInCpd) {
    fields = fields.map((f) =>
      f.name === "cpd"
        ? {
            ...f,
            props: { ...(f.props || {}), showSfcLicences: false },
          }
        : f
    );
  }

  if (consultantViewFiles) {
    fields = fields.map((f) =>
      f.name === "fileGroups"
        ? {
            ...f,
            props: { ...(f.props || {}), consultantView: true },
          }
        : f
    );
  }

  if (readOnly) {
    fields = fields.map((f) => {
      if (f.name === "providerLogin" && editableProviderLogin) {
        return {
          ...f,
          props: {
            ...(f.props || {}),
            readOnly: false,
            editableInShowMode: true,
          },
        };
      }
      if (
        [
          "consultantInfo.licences",
          "consultantInfo.sfcLicences",
          "consultantInfo.certificates",
          "providerLogin",
        ].includes(f.name)
      ) {
        return { ...f, props: { ...(f.props || {}), readOnly: true } };
      }
      return f;
    });
  }

  return {
    ...schema,
    layout: { ...schema.layout, containers, fields },
  };
};

export const getUserDetailFormSchema = (t, role, options = {}) => {
  const {
    title = "User",
    titleField = "name",
    displayMode = ENUM_FORM_DISPLAY.LINK,
    showSaveBar,
    tabKeys = null,
    includeActionList = true,
    includeOverriding = true,
    readOnly = false,
    editableProviderLogin = false,
    showSfcLicencesInCpd = true,
    consultantViewFiles = false,
    hideInternalFiles = false,
  } = options;

  const schema = {
    title,
    titleField,
    display: {
      mode: displayMode,
      size: "1000px", //xs, sm, xl  ....100%
      // position:"right"
    },
    ...(showSaveBar !== undefined ? { showSaveBar } : {}),
    apiEntity,
    initialValues,
    layout: {
      containers: [
        {
          key: "main-g",
          parent: null,
          type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
          props: {
            gutter: "xl",
            columns: 24,
          },
          cols: [
            {
              key: "main-g1",
              props: {
                span: 5,
              },
            },
            {
              key: "main-g2",
              props: {
                span: 19,
              },
            },
          ],
        },
        {
          key: "main-tab",
          parent: "main-g2",
          type: ENUM_FORM_LAYOUT_CONTAINER.TABS,
          props: {
            variant: "default", //default | pills | outline
            defaultValue: "Overview",
          },
          tab: [
            {
              key: "tab-overview",
              label: "Overview",
              icon: IconSettings,
            },
            {
              key: "tab-order",
              label: "Orders",
              icon: IconSettings,
            },
            {
              key: "tab-client",
              label: "Clients",
              icon: IconSettings,
            },
            {
              key: "tab-bankInfo",
              label: "Bank Info",
              icon: IconSettings,
            },
            {
              key: "tab-personalTrading",
              label: "Personal Trading",
              icon: IconSettings,
            },
            {
              key: "tab-licences",
              label: "Licences",
              icon: IconSettings,
            },
            {
              key: "tab-cpd",
              label: "CPD & CPT",
              icon: IconSettings,
            },
            {
              key: "tab-allowance",
              label: "Allowance",
              icon: IconSettings,
            },
            {
              key: "tab-providerLogin",
              label: "Provider Login",
              icon: IconSettings,
            },
            {
              key: "tab-files",
              label: "Files",
              icon: IconSettings,
            },
            {
              key: "tab-logs",
              label: "Logs",
              icon: IconSettings,
            },
          ],
        },
        {
          key: "section-basic",
          parent: "tab-overview",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "User Profile",
            description: "Please fill in the basic information of user",
            withBorder: false,

            mt: 20,
          },
        },
        {
          key: "section-contact-info",
          parent: "tab-overview",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Contact Info",
            withBorder: false,
            mt: 20,
            mb: 20,
          },
        },
        {
          key: "tab-contact-info",
          parent: "section-contact-info",
          type: ENUM_FORM_LAYOUT_CONTAINER.TABS,
          props: {
            variant: "outline",
            size: "sm",
            defaultValue: "Phone Numbers",
          },
          tab: [
            {
              key: "tab-phones",
              label: "Phone Numbers",
            },
            {
              key: "tab-emails",
              label: "Emails",
            },
            {
              key: "tab-addresses",
              label: "Address",
            },
            {
              key: "tab-related-persons",
              label: "Related Person Info",
            },
          ],
        },

        {
          key: "section-employment-info",
          parent: "tab-overview",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Employment Info",
            description: "Please fill in the basic information of user",
            withBorder: false,

            mt: 20,
          },
        },

        {
          key: "section-providerLogin",
          parent: "tab-providerLogin",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Provider Login List",
            description: "Please fill in the basic information of user",
            withBorder: false,

            mt: 20,
          },
        },
        // {
        //   key: "section-info",
        //   parent: "tab-overview",
        //   type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        //   props: {
        //     title: "User Profile",
        //     description: "Please fill in the basic information of user",
        //     withBorder: false,

        //     mt: 20,
        //   },
        // },

        // {
        //   key: "section-employment",
        //   parent: "tab-overview",
        //   type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        //   props: {
        //     title: "Employment Info",
        //     description: "The employment information section",
        //     withBorder: false,
        //     mt: 20,
        //   },
        // },

        {
          key: "section-order",
          parent: "tab-order",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Order List",
            description: "",
            withBorder: false,
            mt: 20,
          },
        },
        {
          key: "section-client",
          parent: "tab-client",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Client List",
            description: "",
            withBorder: false,
            mt: 20,
          },
        },
        {
          key: "section-ops",
          parent: "tab-overview",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Overriding Info",
            description: "",
            withBorder: false,

            mt: 20,
          },
        },

        {
          key: "basicInfo-g",
          parent: "section-basic",
          type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
          props: {
            columns: 24,
          },
          cols: [
            {
              key: "basicInfo-g1",
              props: {
                span: 12,
              },
            },
            {
              key: "basicInfo-g2",
              props: {
                span: 12,
              },
            },
          ],
        },
        {
          key: "employmentEdit-g",
          parent: "section-employment-info",
          type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
          props: {
            columns: 24,
          },
          cols: [
            {
              key: "employmentEdit-g1",
              props: {
                span: 12,
              },
            },
            {
              key: "employmentEdit-g2",
              props: {
                span: 12,
              },
            },
          ],
        },

        {
          key: "section-licences",
          parent: "tab-licences",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "IA Licences",
            description: "Insurance Authority licence types",
            withBorder: false,
            ml: 0,
            mt: 20,
          },
        },
        {
          key: "section-SFC-licences",
          parent: "tab-licences",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "SFC Licences",
            description: "Securities and Futures Commission licence types",
            withBorder: false,
            ml: 0,
            mt: 20,
          },
        },
        {
          key: "section-IA",
          parent: "tab-licences",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "IA",
            description: "IA Registeration Details",
            withBorder: false,
            ml: 0,
            mt: 20,
          },
        },
        {
          key: "section-MPFA",
          parent: "tab-licences",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "MPFA",
            description: "MPF Registeration Details",
            withBorder: false,
            ml: 0,
            mt: 20,
          },
        },
        {
          key: "section-SFC",
          parent: "tab-licences",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "SFC",
            description: "SFC registration details",
            withBorder: false,
            ml: 0,
            mt: 20,
          },
        },
        {
          key: "section-certificate",
          parent: "tab-licences",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "IIQE Certificate",
            description: "IIQE Certification Records",
            withBorder: false,
            ml: 0,
            mt: 20,
          },
        },

        {
          key: "section-files",
          parent: "tab-files",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Files",
            titleOrder: 5,
            // description: "Please assign the corresponding consultant",
            withBorder: false,
            minHeight: "0px",
            mt: 20,
            mb: 20,
          },
        },

        {
          key: "section-file-groups",
          parent: "tab-files",
          type: ENUM_FORM_LAYOUT_CONTAINER.EMPTY,
          props: {},
        },

        // {
        // 	key: "section-overriding",
        // 	parent: "tab-overview",
        // 	type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        // 	props: {
        // 		title: "Commission Setting",
        // 		titleOrder: 5,
        // 		// description: "Please assign the corresponding consultant",
        // 		withBorder: false,
        // 		minHeight: "200px",
        // 		mt: 20,
        // 		mb: 20,
        // 	},
        // },

        {
          key: "section-payment",
          parent: "tab-bankInfo",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Payment Method",
            titleOrder: 5,
            // description: "Please assign the corresponding consultant",
            withBorder: false,
            minHeight: "500px",
            mt: 20,
            mb: 20,
          },
        },
        {
          key: "section-personalTrading",
          parent: "tab-personalTrading",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Personal Trading Accounts",
            description:
              "Declare personal securities trading accounts for compliance.",
            titleOrder: 5,
            withBorder: false,
            mt: 20,
            mb: 20,
          },
        },
        {
          key: "section-allowance",
          parent: "tab-allowance",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            // title: "Allowance List",
            titleOrder: 5,
            // description: "Please assign the corresponding consultant",
            withBorder: false,
            //   minHeight: "500px",
            mt: 20,
            mb: 20,
          },
        },
        {
          key: "section-log",
          parent: "tab-logs",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Logs",
            titleOrder: 5,
            // description: "Please assign the corresponding consultant",
            withBorder: false,
            mt: 20,
            mb: 20,
          },
        },
      ],

      fields: [
        {
          name: "basic",
          component: UserBasicInfo,
          parent: "section-basic",
          props: {},
        },
        // {
        //   name: "basic2",
        //   component: UserSettingInfo,
        //   parent: "section-setting-info",
        //   props: {},
        // },

        {
          name: "basic3",
          component: UserEmploymentInfo,
          parent: "section-employment-info",
          props: {},
        },
        {
          name: "image",
          component: ImagePicker,
          parent: "main-g1",
          props: {
            size: 160,
          },
        },
        {
          name: "UserActionList",
          component: UserActionList,
          parent: "main-g1",
          props: {
            role,
          },
        },

        {
          name: "orders",
          parent: "section-order",
          component: UserOrderHistory,
          props: {},
        },
        {
          name: "clients",
          parent: "section-client",
          component: UserClientList,
          props: {},
        },

        {
          name: "cpd",
          parent: "tab-cpd",
          component: UserCPDDetail,
          props: {},
        },
        {
          name: "userCategory",
          component: Select,
          parent: "basicInfo-g1",
          props: {
            required: true,
            label: "User Category",
            placeholder: "",
            data: DATA_USER_CATEGORY,
            defaultValue: "INDIVIDUAL",
            searchable: true,
          },
        },
        {
          name: "name",
          component: TextInput,
          parent: "basicInfo-g1",
          props: {
            required: true,
            placeholder: "Chan Tai Man",
            label: "English Name",
          },
        },
        {
          name: "cname",
          component: TextInput,
          parent: "basicInfo-g1",
          props: {
            placeholder: "陳大文",
            label: "Chinese Name",
          },
        },
        {
          name: "aliasName",
          component: TextInput,
          parent: "basicInfo-g1",
          props: {
            placeholder: "Kenny",
            label: "Alias Name",
          },
        },
        {
          name: "hkid",
          component: TextInput,
          parent: "basicInfo-g1",
          props: {
            required: true,
            label: "HKID",
            placeholder: "HKID / PASSPORT / BR",
            transform: "",
          },
        },

        {
          name: "phones",
          component: PhoneNumberList,
          parent: "tab-phones",
          props: {},
        },
        {
          name: "emails",
          component: EmailList,
          parent: "tab-emails",
          props: {},
        },
        {
          name: "addresses",
          component: AddressList,
          parent: "tab-addresses",
          props: {},
        },
        {
          name: "contacts",
          component: ContactList,
          parent: "tab-related-persons",
          props: {
            phoneField: "tel",
          },
        },
        {
          name: "birthday",
          component: DatePicker,
          parent: "basicInfo-g1",
          type: "datePicker",
          props: {
            required: true,
            label: "Date of Birth",
            zIndex: 1000,
            placeholder: "Pick Date",
            allowFreeInput: true,
          },
        },

        {
          name: "userRole",
          component: RemoteSelect2,
          parent: "basicInfo-g2",
          props: {
            label: "User Role",
            apiEntity: "userRole",
            required: true,
            valueField: "_id",
            labelField: "name",
            searchFields: ["name"],
            sort: {
              by: "name",
              order: "asc",
            },
          },
        },
        {
          name: "educationLevel",
          component: Select,
          parent: "basicInfo-g2",
          props: {
            label: "Education Level",
            placeholder: "",
            data: DATA_EDUCATION_LEVEL,
            defaultValue: "UG",
            searchable: true,
          },
        },

        {
          name: "gender",
          component: Select,
          parent: "basicInfo-g2",
          props: {
            label: "Gender",
            placeholder: "",
            data: DATA_GENDERS,
            defaultValue: "",
            searchable: true,
          },
        },
        {
          name: "email",
          component: TextInput,
          parent: "basicInfo-g2",
          props: {
            required: true,
            placeholder: "taiman@gmail.com",
            label: "Login Email",
          },
        },
        {
          name: "mariage",
          component: Select,
          parent: "basicInfo-g2",
          props: {
            label: "Mariage Status",
            placeholder: "",
            data: DATA_MARIAGE_STATUS,
            defaultValue: "Single",
            searchable: true,
          },
        },
        {
          name: "teams",
          component: RemoteSelect2,
          parent: "basicInfo-g2",
          props: {
            label: "Teams",
            apiEntity: "team",
            valueField: "_id",
            labelField: "name",
            searchFields: ["name"],
            sort: {
              by: "name",
              order: "asc",
            },
            multiple: true,
          },
        },
        // {
        // 	name: "isConsultant",
        // 	component: Switch,
        // 	parent: "employment-g2",
        // 	props: {
        // 		type: "checkbox",
        // 		label: "Is Consultant",
        // 		mt: 20,
        // 	},
        // },
        {
          name: "staffId",
          component: UniqueTextInput,
          parent: "employmentEdit-g1",
          props: {
            required: true,
            uniqueEntity: "user",
            errorMessage: "Staff ID is already exist.",
            label: "Staff ID",
            placeholder: "AA001",
          },
        },
        {
          name: "userType",
          component: Select,
          parent: "employmentEdit-g1",
          props: {
            required: true,
            label: "User Type",
            placeholder: "Consultant / Channel / Back office",
            data: DATA_USER_TYPES,
            defaultValue: "Consultant",
            searchable: true,
          },
        },
        {
          name: "accessCardNumber",
          component: TextInput,
          parent: "employmentEdit-g1",
          props: {
            label: "Access Card Number",
          },
        },
        {
          name: "octopusNumber",
          component: TextInput,
          parent: "employmentEdit-g1",
          props: {
            label: "Octopus Number",
          },
        },
        {
          name: "workTitle",
          component: TextInput,
          parent: "employmentEdit-g2",
          props: {
            label: "Work Title",
          },
        },

        {
          name: "parent",
          component: RemoteSelect2,
          parent: "section-ops",

          props: {
            label: "Upper Line",
            apiEntity: "user",
            valueField: "_id",
            labelField: "name",
            labelRender:
              // (data) => `${data?.staffId + data?.name}`,
              (data) =>
                `[${data?.staffId}] ${data?.name ?? ""} ${data?.cname ?? ""} ${
                  data?.aliasName ?? ""
                }`,
            searchFields: ["name", "staffId", "aliasName"],
            clearable: true,
            sort: {
              by: "name",
              order: "asc",
            },
          },
        },
        {
          name: "consultantInfo.overridingPlan",
          component: RemoteSelect2,
          parent: "section-ops",
          props: {
            required: true,
            label: "Current Overriding Plan",
            apiEntity: "commissionOverridingPlan",
            valueField: "_id",
            labelField: "name",
            searchFields: ["name"],
            sort: {
              by: "name",
              order: "asc",
            },
          },
        },
        {
          name: "employmentDate",
          component: DatePicker,
          parent: "employmentEdit-g2",
          type: "datePicker",
          props: {
            label: "Join Date",
            zIndex: 1000,
            placeholder: "Pick Date",
            allowFreeInput: true,
          },
        },
        {
          name: "termination.resignDate",
          component: DatePicker,
          parent: "employmentEdit-g2",
          type: "datePicker",
          props: {
            label: "Resignation Date",
            zIndex: 1000,
            placeholder: "Pick Date",
            allowFreeInput: true,
          },
        },
        {
          name: "qualifications",
          component: Textarea,
          parent: "employmentEdit-g2",
          props: {
            label: "Qualifications",
            minRows: 2,
            autosize: true,
          },
        },

        {
          name: "providerLogin",
          component: OrderList,
          parent: "section-providerLogin",

          props: {
            onEnterSave: false,
            defaultRowValue: {},
            row: {
              component: ProviderLoginInfoRow,
            },
          },
        },

        {
          name: "consultantInfo.licences",
          component: LicenceCheckList,
          parent: "section-licences",
          props: {
            label: "Licences",
          },
        },
        {
          name: "consultantInfo.sfcLicences",
          component: SfcLicenceCheckList,
          parent: "section-SFC-licences",
          props: {
            label: "SFC Licences",
          },
        },
        {
          name: "consultantInfo.iaNumber",
          component: TextInput,
          parent: "section-IA",
          props: {
            label: "IA Number",
          },
        },
        {
          name: "consultantInfo.iaRole",
          component: Select,
          parent: "section-IA",
          props: {
            label: "IA Role",
            data: LICENCE_ROLE_OPTIONS,
          },
        },
        {
          name: "consultantInfo.iaFirstRegisterDate",
          component: DatePicker,
          parent: "section-IA",
          type: "datePicker",

          props: {
            label: "First Registration Date",
            zIndex: 1000,
            placeholder: "First Register Date",
            allowFreeInput: true,
          },
        },
        {
          name: "consultantInfo.iaApplyDate",
          component: DatePicker,
          parent: "section-IA",
          type: "datePicker",

          props: {
            label: "Start Date",
            zIndex: 1000,
            placeholder: "Start Date",
            allowFreeInput: true,
          },
        },

        {
          name: "consultantInfo.iaEffectiveDate",
          component: DatePicker,
          parent: "section-IA",
          type: "datePicker",

          props: {
            label: "End Date",
            zIndex: 1000,
            placeholder: "End Date",
            allowFreeInput: true,
          },
        },
        {
          name: "consultantInfo.iaTerminationDate",
          component: DatePicker,
          parent: "section-IA",
          type: "datePicker",

          props: {
            label: "Termination Date",
            zIndex: 1000,
            placeholder: "Termination Date",
            allowFreeInput: true,
          },
        },

        {
          name: "consultantInfo.mpfaNumber",
          component: TextInput,
          parent: "section-MPFA",
          props: {
            label: "MPFA Number",
          },
        },
        {
          name: "consultantInfo.mpfaFirstRegisterDate",
          component: DatePicker,
          parent: "section-MPFA",
          type: "datePicker",
          props: {
            label: "First Registration Date",
            zIndex: 1000,
            placeholder: "First Register Date",
            allowFreeInput: true,
          },
        },
        {
          name: "consultantInfo.mpfaRole",
          component: Select,
          parent: "section-MPFA",
          props: {
            label: "MPFA Role",
            data: MPFA_ROLE_OPTIONS,
          },
        },
        {
          name: "consultantInfo.mpfaEffectiveDate",
          component: DatePicker,
          parent: "section-MPFA",
          type: "datePicker",

          props: {
            label: "Effective Date",
            zIndex: 1000,
            placeholder: "Apply Date",
            allowFreeInput: true,
          },
        },
        {
          name: "consultantInfo.mpfaTerminationDate",
          component: DatePicker,
          parent: "section-MPFA",
          type: "datePicker",
          props: {
            label: "Termination Date",
            zIndex: 1000,
            placeholder: "Termination Date",
            allowFreeInput: true,
          },
        },

        {
          name: "consultantInfo.sfcNumber",
          component: TextInput,
          parent: "section-SFC",
          props: {
            label: "SFC Licence Number",
          },
        },
        {
          name: "consultantInfo.sfcRole",
          component: Select,
          parent: "section-SFC",
          props: {
            label: "SFC Role",
            data: SFC_LICENCE_ROLE_OPTIONS,
          },
        },
        {
          name: "consultantInfo.sfcEffectiveDate",
          component: DatePicker,
          parent: "section-SFC",
          type: "datePicker",
          props: {
            label: "Effective Date",
            zIndex: 1000,
            placeholder: "Effective Date",
            allowFreeInput: true,
          },
        },
        {
          name: "consultantInfo.sfcTerminationDate",
          component: DatePicker,
          parent: "section-SFC",
          type: "datePicker",
          props: {
            label: "Termination Date",
            zIndex: 1000,
            placeholder: "Termination Date",
            allowFreeInput: true,
          },
        },

        {
          name: "files",
          component: FileList,
          parent: "section-files",
          props: {
            enableValidate: true,
            manageFilesLocally: true,
          },
        },

        {
          name: "fileGroups",
          component: UserFileGroupList,
          parent: "section-file-groups",
          props: {},
        },

        {
          name: "consultantInfo.certificates",
          component: ConsultantCertificateList,
          parent: "section-certificate",
          props: {},
        },

        {
          name: "termination.bankInfoCleanDate",
          component: DatePicker,
          parent: "section-payment",
          visibleOnly: (formValues) =>
            formValues?.status === "TERMINATED" &&
            formValues?.termination?.bankInfoCleanDate,
          props: {
            readOnly: true,
            label: "Bank Info Cleanup Date",
            description:
              "Bank account details will be automatically removed on this date.",
            placeholder: "Pick Date",
            allowFreeInput: true,
          },
        },
        {
          name: "termination.bankInfoCleanedAt",
          component: DatePicker,
          parent: "section-payment",
          visibleOnly: (formValues) =>
            formValues?.status === "TERMINATED" &&
            formValues?.termination?.bankInfoCleanedAt,
          props: {
            readOnly: true,
            label: "Bank Info Cleared On",
            placeholder: "Pick Date",
            allowFreeInput: true,
          },
        },
        {
          name: "termination.profileDisposalDate",
          component: DatePicker,
          parent: "section-payment",
          visibleOnly: (formValues) =>
            formValues?.status === "TERMINATED" &&
            formValues?.termination?.profileDisposalDate,
          props: {
            readOnly: true,
            label: "Profile Disposal Date",
            description:
              "Profile will be marked for disposal on this date (7 years after effective date).",
            placeholder: "Pick Date",
            allowFreeInput: true,
          },
        },
        {
          name: "disposalStatus",
          component: Select,
          parent: "section-payment",
          visibleOnly: (formValues) => formValues?.status === "TERMINATED",
          props: {
            readOnly: true,
            label: "Disposal Status",
            data: DATA_USER_DISPOSAL_STATUS,
            placeholder: "Select status",
          },
        },

        {
          name: "consultantInfo.paymentMethods.type",
          component: Chips,
          parent: "section-payment",
          props: {
            required: true,
            // label: "Type",
            defaultValue: "Bank Transfer",
            items: DATA_CONSULTANT_PAYMENT_METHOD,
          },
        },
        {
          name: "consultantInfo.paymentMethods.bank",
          component: Select,
          parent: "section-payment",
          props: {
            required: true,
            label: "Bank",
            data: DATA_BANK_WITH_CODE,
            dropdownPosition: "bottom",
            searchable: true,
          },
        },
        {
          name: "consultantInfo.paymentMethods.name",
          component: TextInput,
          parent: "section-payment",
          props: {
            required: true,
            label: "Name on Account",
          },
        },

        {
          name: "consultantInfo.paymentMethods.accNumber",
          component: TextInput,
          parent: "section-payment",
          props: {
            required: true,
            label: "Account Number",
          },
        },
        {
          name: "consultantInfo.personalTradingAccounts",
          component: OrderList,
          parent: "section-personalTrading",
          props: {
            onEnterSave: false,
            addLabel: "Add Trading Account",
            defaultRowValue: {},
            listHeader: PersonalTradingAccountListHeader,
            row: {
              component: PersonalTradingAccountRow,
            },
          },
        },
        {
          name: "consultantInfo.allowances",
          component: ConsultantAllowanceList,
          parent: "section-allowance",
          props: {},
        },

        {
          name: "overridingPlans",
          component: ConsultantOverridingPlans,
          parent: "section-ops",
          props: {},
        },
        {
          name: "log",
          component: ActivityLog,
          parent: "section-log",
          props: {},
        },

        {
          name: "confirm",
          component: DummyWidget,
          parent: "section-confirm",
          props: {
            dummy: (
              <Text>
                Please confirm the client information is correct and updated.
              </Text>
            ),
          },
        },
      ],
    },
  };

  return applyUserDetailOptions(schema, {
    tabKeys,
    includeActionList,
    includeOverriding,
    readOnly,
    editableProviderLogin,
    showSfcLicencesInCpd,
    consultantViewFiles,
    hideInternalFiles,
    showProfileSummary: options.showProfileSummary,
  });
};
